import { useState, useRef, useEffect, useCallback } from 'react';
import { useReactFlow, Node, Edge } from '@xyflow/react';
import { Play, StopCircle, Loader2 } from 'lucide-react';
import { getVeoConfig, generateVeoVideo, getVeoTaskStatus } from '../../../../utils/api';
import type { VeoConfig, VeoGenerateParams, VeoOption } from '../../../../types';

const GenVideoContent = ({ data, id }: { data: any, id: string }) => {
  const { updateNodeData, getEdges, getNode, addNodes, addEdges, deleteElements } = useReactFlow();
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [config, setConfig] = useState<VeoConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState<string>(data.currentMode || 'text');

  // 用于存储轮询定时器，以便取消
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current as unknown as ReturnType<typeof setTimeout>);
        pollTimerRef.current = null;
      }
    };
  }, []);

  // 加载配置信息
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoadingConfig(true);
        setError(null);
        const response = await getVeoConfig();
        setConfig(response.data);
      } catch (err) {
        console.error('Failed to load Veo config:', err);
        setError('Failed to load configuration. Using default settings.');
      } finally {
        setLoadingConfig(false);
      }
    };

    loadConfig();
  }, []);

  const update = (key: string, value: any) => {
    updateNodeData(id, { [key]: value });
  };

  // 根据分辨率获取可用时长选项
  const getAvailableDurations = useCallback((resolution: string): VeoOption[] => {
    if (!config) return [];

    const resolutionConfig = config.resolution_options.find(r => r.value === resolution);
    if (resolutionConfig?.max_duration) {
      // 1080p和4k只支持8秒
      return config.duration_options.filter(d => parseInt(d.value) === resolutionConfig.max_duration);
    }
    // 720p支持所有时长
    return config.duration_options;
  }, [config]);

  // 当分辨率变化时，如果当前时长不可用，则重置为8秒
  const handleResolutionChange = useCallback((newResolution: string) => {
    update('resolution', newResolution);

    const availableDurations = getAvailableDurations(newResolution);
    const currentDuration = data.duration || 8;
    const isDurationValid = availableDurations.some(d => parseInt(d.value) === currentDuration);

    if (!isDurationValid) {
      update('duration', 8);
    }
  }, [data.duration, update, getAvailableDurations]);

  // 当模式变化时，更新节点数据并清理不兼容的边缘
  useEffect(() => {
    // 先更新节点数据
    update('currentMode', currentMode);

    // 延迟清理，确保节点数据已更新且React Flow已重新渲染
    const timerId = setTimeout(() => {
      try {
        const allEdges = getEdges();
        const incomingEdges = allEdges.filter(edge => edge.target === id);
        const incompatibleEdges: string[] = [];

        incomingEdges.forEach(edge => {
          const inputKey = edge.targetHandle;
          let isCompatible = false;

          switch (currentMode) {
            case 'text':
              isCompatible = inputKey === 'in_text' || inputKey === 'in_text_negative';
              break;
            case 'image':
              isCompatible = inputKey === 'in_text' || inputKey === 'in_text_negative' || inputKey === 'in_img_ref';
              break;
            case 'interpolate':
              // 🔴 移除 in_img_ref（reference_images已禁用）
              isCompatible = inputKey === 'in_text' || inputKey === 'in_text_negative' ||
                            inputKey === 'in_img_first' || inputKey === 'in_img_last';
              break;
            default:
              isCompatible = true;
          }

          if (!isCompatible) {
            incompatibleEdges.push(edge.id);
          }
        });

        if (incompatibleEdges.length > 0) {
          deleteElements({ edges: incompatibleEdges.map(edgeId => ({ id: edgeId })) });
        }
      } catch (error) {
        console.error('Error cleaning up edges:', error);
      }
    }, 200);

    // 清理定时器
    return () => clearTimeout(timerId);
  }, [currentMode]);




  // 构建请求参数
  const buildRequestParams = (): VeoGenerateParams => {
    // 收集输入提示词和图片
    const allEdges = getEdges();
    const incomingEdges = allEdges.filter(edge => edge.target === id);
    let collectedPrompt = "";
    let collectedNegativePrompt = "";
    let collectedImageUrl = "";
    let collectedFirstFrameUrl = "";
    let collectedLastFrameUrl = "";

    incomingEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);

      if (edge.targetHandle === 'in_text' && sourceNode?.data?.text) {
        collectedPrompt += sourceNode.data.text + " ";
      }
      if (edge.targetHandle === 'in_text_negative' && sourceNode?.data?.text) {
        collectedNegativePrompt += sourceNode.data.text + " ";
      }
      if (edge.targetHandle === 'in_img_ref' && sourceNode?.data?.url) {
        // 仅用于image模式
        collectedImageUrl = sourceNode.data.url as string;
      }
      if (edge.targetHandle === 'in_img_first' && sourceNode?.data?.url) {
        collectedFirstFrameUrl = sourceNode.data.url as string;
      }
      if (edge.targetHandle === 'in_img_last' && sourceNode?.data?.url) {
        collectedLastFrameUrl = sourceNode.data.url as string;
      }
    });

    const params: VeoGenerateParams = {
      prompt: collectedPrompt.trim() || "Default video prompt",
      negative_prompt: collectedNegativePrompt.trim(),
      resolution: data.resolution || "720p",
      duration: data.duration || 8,
      aspect_ratio: data.aspect_ratio || "16:9",
    };

    // 根据模式添加特定参数
    switch (currentMode) {
      case 'image':
        params.image_url = collectedImageUrl;
        break;
      case 'interpolate':
        params.first_frame_url = collectedFirstFrameUrl;
        params.last_frame_url = collectedLastFrameUrl;
        break;
    }

    return params;
  };

  // --- 核心逻辑：异步轮询方案 ---
  const handleGenerate = async () => {
    if (isProcessing) return; // Prevent double click

    const currentNode = getNode(id);
    if (!currentNode) return;

    setIsProcessing(true);
    setProgress(0);

    // 1. 构建请求参数
    const params = buildRequestParams();

    // 2. Prepare Downstream Node (Result Node)
    let resultNodeId: string | null = null;
    const connectedEdge = getEdges().find(
      (edge) => edge.source === id && edge.sourceHandle === 'out_res'
    );

    if (connectedEdge) {
      resultNodeId = connectedEdge.target;
    } else {
      // Create new result node if not exists
      resultNodeId = `result-video-${Date.now()}`;

      const newNode: Node = {
        id: resultNodeId,
        type: 'result-video',
        position: { x: currentNode.position.x + 350, y: currentNode.position.y },
        data: { label: 'Generated Video', videoUrl: null, status: 'IDLE' },
        style: { width: 256, height: 256 },
      };
      const newEdge: Edge = {
        id: `e-${id}-${resultNodeId}`,
        source: id,
        sourceHandle: 'out_res',
        target: resultNodeId,
        targetHandle: 'in_res',
        animated: true,
        style: { stroke: '#e879f9', strokeWidth: 2 },
      };
      addNodes([newNode]);
      addEdges([newEdge]);
      // Small delay to ensure node is registered
      await new Promise(r => setTimeout(r, 100));
    }

    // Set Result Node to "Initializing"
    if (resultNodeId) {
      updateNodeData(resultNodeId, { status: 'QUEUED', progress: 0, videoUrl: null });
    }

    try {
      // 3. Submit Task (Get Task ID)
      const response = await generateVeoVideo(params);
      const taskId = response.data.task_id;

      // 4. Start Polling Loop
      startTaskPolling(taskId, resultNodeId);

    } catch (error) {
      console.error("Submission failed:", error);
      setIsProcessing(false);
      setError('Failed to submit task. Please try again.');
      if (resultNodeId) updateNodeData(resultNodeId, { status: 'ERROR' });
    }
  };

  // 开始任务轮询
  const startTaskPolling = (taskId: string, resultNodeId: string | null) => {
    let pollCount = 0;
    const maxPolls = 60;
    const maxRetries = 3;
    let retryCount = 0;

    // 渐进式轮询间隔
    const getPollInterval = (attempt: number) => {
      if (attempt < 6) return 5000;   // 前30秒: 5秒
      if (attempt < 12) return 8000;  // 30秒-1分钟: 8秒
      return 10000;                    // 1分钟后: 10秒
    };

    const poll = async () => {
      if (pollCount >= maxPolls) {
        // 轮询超时
        clearTimeout(pollTimerRef.current as unknown as ReturnType<typeof setTimeout>);
        pollTimerRef.current = null;
        setIsProcessing(false);
        setError('Task timeout. Please check task status later.');
        if (resultNodeId) updateNodeData(resultNodeId, { status: 'ERROR' });
        return;
      }

      try {
        // Check Status
        const response = await getVeoTaskStatus(taskId);
        const task = response.data;

        // 重置重试计数
        retryCount = 0;

        // Update local state
        setProgress(task.progress || 0);

        // Update Downstream Node State (Visible to user on the result node)
        if (resultNodeId) {
          updateNodeData(resultNodeId, {
            status: task.status,
            progress: task.progress || 0
          });
        }

        if (task.status === 'completed') {
          // Success!
          clearTimeout(pollTimerRef.current as unknown as ReturnType<typeof setTimeout>);
          pollTimerRef.current = null;
          setIsProcessing(false);
          setProgress(100);

          if (resultNodeId && task.video_url) {
            updateNodeData(resultNodeId, {
              status: 'COMPLETED',
              videoUrl: task.video_url,
              timestamp: Date.now()
            });
          }
        } else if (task.status === 'failed') {
          // Failure
          clearTimeout(pollTimerRef.current as unknown as ReturnType<typeof setTimeout>);
          pollTimerRef.current = null;
          setIsProcessing(false);
          setError(task.error_message || 'Generation failed');
          if (resultNodeId) updateNodeData(resultNodeId, { status: 'ERROR' });
        }

      } catch (pollError) {
        console.error("Polling error:", pollError);

        // 错误处理和重试
        retryCount++;
        if (retryCount <= maxRetries) {
          // 短暂等待后重试
          console.log(`Retrying poll (${retryCount}/${maxRetries})...`);
        } else {
          // 重试次数用完，继续轮询
          retryCount = 0;
        }
      } finally {
        pollCount++;
      }
    };

    // 立即执行一次轮询
    poll();

    // 然后开始定时轮询，使用渐进式间隔
    const pollWithProgressiveInterval = () => {
      poll();
      if (pollTimerRef.current) {
        const interval = getPollInterval(pollCount);
        setTimeout(pollWithProgressiveInterval, interval);
      }
    };

    // 清除可能存在的旧定时器
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
    }

    // 使用setTimeout实现渐进式轮询
    const interval = getPollInterval(pollCount);
    pollTimerRef.current = setTimeout(pollWithProgressiveInterval, interval) as unknown as ReturnType<typeof setInterval>;
  };

  const handleCancel = async () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current as unknown as ReturnType<typeof setTimeout>);
      pollTimerRef.current = null;
    }
    setIsProcessing(false);
    setProgress(0);
    setError(null);
  };

  return (
    <div className="flex flex-col gap-3 p-1">
      {/* 配置加载状态 */}
      {loadingConfig ? (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          <p className="text-xs text-slate-400 mt-2">Loading configuration...</p>
        </div>
      ) : (
        <>
          {/* 错误提示 */}
          {error && (
            <div className="bg-red-900/30 border border-red-800 rounded p-2">
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}

          {/* 模式选择器 */}
          <div>
            <label className="text-[10px] text-slate-400 block mb-1">Generation Mode</label>
            <select
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs outline-none nodrag text-slate-200"
              value={currentMode}
              onChange={(e) => {
                const newMode = e.target.value;
                setCurrentMode(newMode);
                setError(null);
              }}
              disabled={isProcessing}
            >
              {config?.supported_modes.map((mode) => (
                <option key={mode.mode} value={mode.mode}>
                  {mode.label}
                </option>
              )) || []}
            </select>
          </div>

          {/* 分辨率选择 */}
          <div>
            <label className="text-[10px] text-slate-400 block mb-1">Resolution</label>
            <select
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs outline-none nodrag text-slate-200"
              value={data.resolution || "720p"}
              onChange={(e) => handleResolutionChange(e.target.value)}
              disabled={isProcessing}
            >
              {config?.resolution_options.map((res) => (
                <option key={res.value} value={res.value}>
                  {res.label} - {res.description}
                </option>
              )) || []}
            </select>
          </div>

          {/* 时长选择 */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-400 block mb-1">Duration</label>
              <select
                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs outline-none nodrag text-slate-200"
                value={data.duration || 8}
                onChange={(e) => update('duration', parseInt(e.target.value))}
                disabled={isProcessing}
              >
                {getAvailableDurations(data.resolution || "720p").map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 宽高比选择 */}
            <div>
              <label className="text-[10px] text-slate-400 block mb-1">Aspect Ratio</label>
              <select
                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs outline-none nodrag text-slate-200"
                value={data.aspect_ratio || "16:9"}
                onChange={(e) => update('aspect_ratio', e.target.value)}
                disabled={isProcessing}
              >
                {config?.aspect_ratio_options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                )) || []}
              </select>
            </div>
          </div>

          {/* Action Button: Generate or Cancel */}
          {isProcessing ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
              className="nodrag w-full flex items-center justify-center gap-2 bg-red-900/50 hover:bg-red-900 border border-red-800 text-red-200 text-xs font-bold py-2 px-2 rounded transition mt-1"
            >
              <StopCircle size={14} />
              CANCEL ({progress}%)
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleGenerate();
              }}
              className="nodrag w-full flex items-center justify-center gap-2 bg-pink-700 hover:bg-pink-600 border border-pink-600 text-white text-xs font-bold py-2 px-2 rounded transition shadow-lg shadow-pink-900/20 active:scale-95 mt-1"
            >
              <Play size={14} fill="currentColor" />
              GENERATE VIDEO
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default GenVideoContent;